import { useEffect, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useParams } from "@tanstack/react-router";
import type { CurrentSessionResponse } from "@snowiespace/protocol";
import {
  BrandMark,
  Icon,
  ProductShell,
  Sidebar,
  SidebarFooter,
  SidebarSection,
  SelectMenu,
  SnowieIdentity,
  Stage,
} from "@snowiespace/ui/web";
import { client } from "./api.js";
import { catalogIcon } from "./catalog-presentation.js";
import { SpaceEventsContext, SpaceEventStatusContext, useSpaceEvents } from "./events.js";
import { ui } from "./styles.js";

function NavContent({ icon, label }: { icon: Parameters<typeof Icon>[0]["name"]; label: ReactNode }) {
  return <><Icon name={icon} /><span>{label}</span></>;
}

const navClass = ui("nav-link");
const navActiveClass = ui("nav-link", "nav-link--active");
const spacesLandingOption = { id: "__snowiespace_spaces__", label: "All Spaces", description: "Account and Space settings" };

export function Shell({ session }: { session: CurrentSessionResponse }) {
  const params = useParams({ strict: false }) as { spaceId?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const spaceId = params.spaceId;
  const selected = session.spaces.find((space) => space.id === spaceId);
  const bootstrap = useQuery({
    queryKey: ["bootstrap", spaceId],
    queryFn: () => client.bootstrap(spaceId!),
    enabled: Boolean(selected),
    refetchInterval: 10_000,
  });
  const config = useQuery({ queryKey: ["config"], queryFn: client.config });
  const composition = useQuery({
    queryKey: ["composition", selected?.id],
    queryFn: () => client.composition(selected!.id),
    enabled: Boolean(selected),
    refetchInterval: 10_000,
  });
  const snowieProfile = useQuery({
    queryKey: ["snowie", "profile", selected?.id],
    queryFn: () => client.snowieProfile(selected!.id),
    enabled: Boolean(selected),
  });
  const conversations = useQuery({
    queryKey: ["snowie", "conversations", selected?.id],
    queryFn: () => client.conversations(selected!.id),
    enabled: Boolean(selected),
    refetchInterval: 2_000,
  });
  const { events, status } = useSpaceEvents(selected?.id);
  const modules = bootstrap.data?.modules ?? [];
  const releasedApps = composition.data?.active.manifest.appReleases ?? [];
  const frameworkModuleIds = new Set(releasedApps.flatMap((release) => composition.data?.active.manifest.packages.filter((item) => item.digest === release.packageDigest).map((item) => item.moduleId) ?? []));
  const destinations = modules.filter((module) => module.view.kind !== "app" && module.view.kind !== "schedule" && !frameworkModuleIds.has(module.id));
  const workbenches = modules.filter((module) => module.view.kind === "app" || module.view.kind === "schedule");
  const spaceOptions = [spacesLandingOption, ...session.spaces.map((space) => ({ id: space.id, label: space.name, description: `${space.role} · ${space.status}` }))];
  const moduleLabel = (module: typeof modules[number]) => module.view.kind === "schedule" ? "Automation" : module.title;

  useEffect(() => {
    if (spaceId && !selected) void navigate({ to: "/", replace: true });
    if (selected) localStorage.setItem("snowiespace.lastSpace", selected.id);
  }, [navigate, selected, spaceId]);

  useEffect(() => {
    const latest = events.at(-1);
    if (!selected || !latest?.type.startsWith("snowie.")) return;
    void queryClient.invalidateQueries({ queryKey: ["snowie", "conversations", selected.id] });
    void queryClient.invalidateQueries({ queryKey: ["snowie", "conversation", selected.id] });
    if (latest.type.startsWith("snowie.memory.")) void queryClient.invalidateQueries({ queryKey: ["snowie", "memory", selected.id] });
    if (latest.type === "snowie.profile.updated") void queryClient.invalidateQueries({ queryKey: ["snowie", "profile", selected.id] });
  }, [events, queryClient, selected]);

  const selectedProvider = snowieProfile.data?.availableProviders.find((provider) => provider.id === snowieProfile.data?.profile.providerProfile);
  const snowieName = `Snowie · ${selectedProvider?.displayName ?? snowieProfile.data?.profile.providerProfile ?? "Ready"}`;
  const snowieKeyRequired = Boolean(selectedProvider?.spaceApiKeySupported && snowieProfile.data?.profile.credentialSource !== "space");
  const activeSnowieSessions = conversations.data?.conversations.filter((conversation) => conversation.state === "active").length ?? 0;

  const moduleLinks = (items: typeof modules) => items.map((module) => (
    <Link
      activeProps={{ className: navActiveClass }}
      className={navClass}
      inactiveProps={{ className: navClass }}
      key={module.id}
      params={{ spaceId: selected!.id, moduleId: module.id }}
      search={{ appId: undefined }}
      to="/spaces/$spaceId/modules/$moduleId"
    >
      <NavContent icon={catalogIcon(module)} label={moduleLabel(module)} />
    </Link>
  ));

  const releasedAppLinks = releasedApps.map((app) => (
    <Link
      activeProps={{ className: navActiveClass }}
      className={navClass}
      inactiveProps={{ className: navClass }}
      key={app.appId}
      params={{ spaceId: selected!.id, appId: app.appId }}
      to="/spaces/$spaceId/apps/$appId"
    >
      <NavContent icon="app" label={app.name} />
    </Link>
  ));

  const navigation = <Sidebar className={ui("console-sidebar")}>
    <SelectMenu
      className={ui("space-switch")}
      label="Switch Space"
      value={selected?.id ?? spacesLandingOption.id}
      options={spaceOptions}
      onChange={(nextSpaceId) => {
        if (nextSpaceId === spacesLandingOption.id) void navigate({ to: "/" });
        else if (nextSpaceId && nextSpaceId !== selected?.id) void navigate({ to: "/spaces/$spaceId", params: { spaceId: nextSpaceId } });
      }}
      renderValue={(option) => <><BrandMark label="" /><span><strong>Snowiespace</strong><small>{option?.label ?? "Choose a Space"}</small></span></>}
    />

    <nav className={ui("sidebar-navigation")} aria-label="Space navigation" data-scroll-owner="space-navigation">
      <SidebarSection>
        <Link activeOptions={{ exact: true }} activeProps={{ className: navActiveClass }} className={navClass} inactiveProps={{ className: navClass }} to={selected ? "/spaces/$spaceId" : "/"} params={selected ? { spaceId: selected.id } : undefined}>
          <NavContent icon="home" label="Home" />
        </Link>
        {selected ? <Link activeProps={{ className: navActiveClass }} className={navClass} inactiveProps={{ className: navClass }} params={{ spaceId: selected.id }} search={{ targetKind: undefined, targetId: undefined, conversationId: undefined, jobId: undefined }} to="/spaces/$spaceId/poke"><NavContent icon="poke" label="Poke" /></Link> : null}
      </SidebarSection>

      {selected && (releasedApps.length || destinations.length) ? <SidebarSection label="Apps">{releasedAppLinks}{moduleLinks(destinations)}</SidebarSection> : null}
      {selected && workbenches.length ? <SidebarSection label="Workbench">{moduleLinks(workbenches)}</SidebarSection> : null}
    </nav>

    <SidebarFooter>
      {selected ? <Link className={ui("snowie-card")} params={{ spaceId: selected.id }} to="/spaces/$spaceId/snowie"><SnowieIdentity name={snowieName} detail={`${snowieKeyRequired ? "API key required" : status === "live" ? "Ready" : status} · ${activeSnowieSessions} running now`} /><span className={ui("snowie-counter")} aria-label={`${activeSnowieSessions} concurrent Snowie sessions`}>{activeSnowieSessions}</span></Link> : null}
      {selected ? <Link activeProps={{ className: navActiveClass }} className={navClass} inactiveProps={{ className: navClass }} params={{ spaceId: selected.id }} to="/spaces/$spaceId/settings"><NavContent icon="settings" label="Settings" /></Link> : null}
      {selected ? <Link activeProps={{ className: navActiveClass }} className={ui("nav-link", "nav-link--subtle")} inactiveProps={{ className: ui("nav-link", "nav-link--subtle") }} params={{ spaceId: selected.id }} to="/spaces/$spaceId/rescue"><NavContent icon="rescue" label="Rescue" /></Link> : null}
      <div className={ui("release-identity")}><span>{config.data?.release ?? "R4"}</span><code>{config.data?.revision.slice(0, 9) ?? "development"}</code></div>
    </SidebarFooter>
  </Sidebar>;

  const mobileNavigation = <>
    <Link activeOptions={{ exact: true }} activeProps={{ className: ui("mobile-nav-link", "mobile-nav-link--active") }} className={ui("mobile-nav-link")} inactiveProps={{ className: ui("mobile-nav-link") }} to={selected ? "/spaces/$spaceId" : "/"} params={selected ? { spaceId: selected.id } : undefined}><Icon name="home" /><span>Home</span></Link>
    {selected ? <Link activeProps={{ className: ui("mobile-nav-link", "mobile-nav-link--active") }} className={ui("mobile-nav-link")} inactiveProps={{ className: ui("mobile-nav-link") }} params={{ spaceId: selected.id }} search={{ targetKind: undefined, targetId: undefined, conversationId: undefined, jobId: undefined }} to="/spaces/$spaceId/poke"><Icon name="poke" /><span>Poke</span></Link> : null}
    {selected ? releasedApps.slice(0, 1).map((app) => <Link activeProps={{ className: ui("mobile-nav-link", "mobile-nav-link--active") }} className={ui("mobile-nav-link")} inactiveProps={{ className: ui("mobile-nav-link") }} key={app.appId} params={{ spaceId: selected.id, appId: app.appId }} to="/spaces/$spaceId/apps/$appId"><Icon name="app" /><span>{app.name}</span></Link>) : null}
    {selected ? destinations.slice(0, releasedApps.length ? 1 : 2).map((module) => <Link activeProps={{ className: ui("mobile-nav-link", "mobile-nav-link--active") }} className={ui("mobile-nav-link")} inactiveProps={{ className: ui("mobile-nav-link") }} key={module.id} params={{ spaceId: selected.id, moduleId: module.id }} search={{ appId: undefined }} to="/spaces/$spaceId/modules/$moduleId"><Icon name={catalogIcon(module)} /><span>{moduleLabel(module)}</span></Link>) : null}
    {selected ? <Link activeProps={{ className: ui("mobile-nav-link", "mobile-nav-link--active") }} className={ui("mobile-nav-link")} inactiveProps={{ className: ui("mobile-nav-link") }} params={{ spaceId: selected.id }} to="/spaces/$spaceId/settings"><Icon name="settings" /><span>Settings</span></Link> : <Link className={ui("mobile-nav-link")} to="/"><Icon name="account" /><span>Account</span></Link>}
  </>;

  return <SpaceEventsContext.Provider value={{ events, status }}>
    <SpaceEventStatusContext.Provider value={status}>
      <ProductShell className={ui("console-shell")} mobileNavigation={mobileNavigation} navigation={navigation}>
        <Stage className={ui("console-stage")} scrollOwner={selected ? "space-stage" : "space-gallery"}><Outlet /></Stage>
      </ProductShell>
    </SpaceEventStatusContext.Provider>
  </SpaceEventsContext.Provider>;
}
